/**
 * Component: ResourceAccessLogPage
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R06
 *
 * Admin page for displaying COUNTER-compliant resource access logs.
 * ADMIN-V1-R06: System shall display COUNTER-compliant guideline and
 *               publication resource access logs.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  FileText,
  Loader2,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { subDays, format } from 'date-fns';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExportButton, type ExportData } from './components/ExportButton';
import { DateRangePicker } from './components/DateRangePicker';

// ── Types ────────────────────────────────────────────────────────────────────

interface ResourceAccessLogEntry {
  id: string;
  timestamp: string;
  user_id: string;
  session_id: string | null;
  correlation_id: string;
  resource_type: string;
  resource_id: string;
  resource_title: string | null;
  access_type: string;
  access_method: string;
  publisher: string | null;
  extra: Record<string, unknown>;
}

interface ResourceAccessLogsResponse {
  logs: ResourceAccessLogEntry[];
  total_count: number;
  offset: number;
  limit: number;
  has_more: boolean;
  total_views: number;
  total_downloads: number;
  unique_resources: number;
  unique_users: number;
}

type SortField = 'timestamp' | 'user_id' | 'resource_type' | 'resource_id' | 'resource_title' | 'access_type' | 'access_method' | 'publisher';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZES = [10, 25, 50] as const;
const RESOURCE_TYPES = ['all', 'guideline', 'publication'] as const;
const ACCESS_TYPES = ['all', 'view', 'download', 'search', 'print'] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function resourceTypeBadgeVariant(type: string): 'default' | 'secondary' | 'outline' {
  switch (type) {
    case 'guideline':
      return 'default';
    case 'publication':
      return 'secondary';
    default:
      return 'outline';
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function ResourceAccessLogPage(): React.ReactElement {
  // Data state
  const [logs, setLogs] = useState<ResourceAccessLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('all');
  const [accessTypeFilter, setAccessTypeFilter] = useState<string>('all');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // ── Fetch logs ──────────────────────────────────────────────────────────

  const fetchLogs = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return;

    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        start_time: dateRange.from.toISOString(),
        end_time: dateRange.to.toISOString(),
        offset: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (resourceTypeFilter !== 'all') {
        params['resource_type'] = resourceTypeFilter;
      }
      if (accessTypeFilter !== 'all') {
        params['access_type'] = accessTypeFilter;
      }

      const response = await api.get<ResourceAccessLogsResponse>('/admin/logs/resources', { params });
      setLogs(response.data.logs);
      setTotalCount(response.data.total_count);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange, page, pageSize, resourceTypeFilter, accessTypeFilter]);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, resourceTypeFilter, accessTypeFilter, pageSize]);

  // ── Sorting ──────────────────────────────────────────────────────────────

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const aVal = String(a[sortField] ?? '').toLowerCase();
      const bVal = String(b[sortField] ?? '').toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [logs, sortField, sortDirection]);

  // ── Sort header button ───────────────────────────────────────────────────

  const SortButton = useCallback(
    ({ field, label }: { field: SortField; label: string }) => {
      const isActiveSort = sortField === field;
      const Icon = isActiveSort
        ? sortDirection === 'asc'
          ? ArrowUp
          : ArrowDown
        : ArrowUpDown;

      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8"
          onClick={() => handleSort(field)}
          aria-label={`Sort by ${label}${isActiveSort ? `, currently ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
        >
          {label}
          <Icon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    },
    [sortField, sortDirection, handleSort],
  );

  // ── Export data ──────────────────────────────────────────────────────────

  const exportData: ExportData = useMemo(() => {
    const dateStr = dateRange?.from && dateRange?.to
      ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
      : 'all-time';

    const rows = logs.map((log) => ({
      Timestamp: log.timestamp,
      User: log.user_id,
      'Resource Type': log.resource_type,
      'Resource ID': log.resource_id,
      'Resource Title': log.resource_title ?? '',
      'Access Type': log.access_type,
      'Access Method': log.access_method,
      Publisher: log.publisher ?? '',
    }));

    return {
      filename: `resource-access-logs_${dateStr}`,
      headers: ['Timestamp', 'User', 'Resource Type', 'Resource ID', 'Resource Title', 'Access Type', 'Access Method', 'Publisher'],
      rows,
    };
  }, [dateRange, logs]);

  // ── Pagination handler ──────────────────────────────────────────────────

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(Number(value));
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6" aria-hidden="true" />
            Resource Access Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            COUNTER-compliant guideline and publication access log entries.
          </p>
        </div>
        <ExportButton data={exportData} disabled={loading} />
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                disabled={loading}
              />
            </div>
            <div className="flex gap-2">
              <Select value={resourceTypeFilter} onValueChange={setResourceTypeFilter}>
                <SelectTrigger className="w-[140px]" aria-label="Filter by resource type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={accessTypeFilter} onValueChange={setAccessTypeFilter}>
                <SelectTrigger className="w-[140px]" aria-label="Filter by access type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCESS_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Access' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs table */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">
            Log Entries
            {!loading && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({totalCount.toLocaleString()} total)
              </span>
            )}
          </CardTitle>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[100px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12" aria-label="Loading logs">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading resource access logs...</span>
            </div>
          ) : sortedLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              No log entries found for the selected filters.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <SortButton field="timestamp" label="Timestamp" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="user_id" label="User" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="resource_type" label="Resource Type" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="resource_id" label="Resource ID" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="resource_title" label="Resource Title" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="access_type" label="Access Type" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="access_method" label="Access Method" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="publisher" label="Publisher" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell className="font-medium max-w-[120px] truncate" title={log.user_id}>
                          {log.user_id}
                        </TableCell>
                        <TableCell>
                          <Badge variant={resourceTypeBadgeVariant(log.resource_type)}>
                            {log.resource_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-[120px] truncate" title={log.resource_id}>
                          {log.resource_id}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={log.resource_title ?? ''}>
                          {log.resource_title ?? '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.access_type}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{log.access_method}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {log.publisher ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ResourceAccessLogPage;
